/**
 * DesktopSuggestions — rechte Desktop-Spalte für 3-Spalten-Layout (≥1440px).
 *
 * P5 (Bereichstrennung §4 / §9):
 *  - Social-Feed: KEINE großen Creator-Cards mehr. Stattdessen zwei kompakte
 *    Bereiche: „Vorschläge für dich" (max 5, kleines Avatar + Name + Folgen-Button)
 *    und „Ich folge" (kompakte Liste der Social-Follows). Nur Social-Identitäten.
 *  - stilltouchedX: oben bis zu 5 neue STX-Artikel, darunter die großen
 *    Creator-Cards (ausschließlich öffentliche Daten aus stx_profiles).
 *  - Marketplace: Neu im MP + Bestellungen + Payouts + Favoriten (unverändert).
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles, ShoppingBag, Wallet, Heart, Star,
  MessageSquare, Users, TrendingUp, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import GuestLoginBox from "@/components/home/GuestLoginBox";
import CreatorSuggestionCard from "@/components/home/CreatorSuggestionCard";
import CompactCreatorRow from "@/components/home/CompactCreatorRow";
import { orderStatusLabel, orderStatusColor } from "@/components/admin/orderStatusUtils";

function useAreaFromPath(pathname) {
  if (pathname.startsWith("/user/") && pathname.endsWith("/marketplace")) return "MP";
  if (pathname.startsWith("/user/") && pathname.endsWith("/stx")) return "STX";
  if (pathname.startsWith("/marketplace")) return "MP";
  if (pathname.startsWith("/stilltouchedx") || pathname.startsWith("/stx") || pathname.startsWith("/x/")) return "STX";
  if (
    pathname === "/" ||
    pathname.startsWith("/reels") ||
    pathname.startsWith("/creator") ||
    pathname.startsWith("/posts") ||
    (pathname.startsWith("/user/") && (pathname.endsWith("/social") || pathname.split("/").length === 3))
  ) return "FEED";
  return null;
}

function isSTXContext(pathname) {
  if (pathname.startsWith("/user/") && pathname.endsWith("/stx")) return true;
  return pathname.startsWith("/stilltouchedx") || pathname.startsWith("/x/");
}

function SectionHeader({ icon: Icon, children, testId, headingClass, noUpper }) {
  return (
    <h3 data-testid={testId} className={`text-sm font-bold ${noUpper ? "" : "uppercase"} tracking-wider ${headingClass} mb-3 flex items-center gap-2`}>
      <Icon size={14} /> {children}
    </h3>
  );
}

const FEED_TABS = [
  { key: "feed",     labelKey: "home.tab_feed",     labelDefault: "Beiträge", icon: MessageSquare, testId: "tab-feed" },
  { key: "creator",  labelKey: "home.tab_creator",  labelDefault: "Creator",  icon: Users,         testId: "tab-creator" },
  { key: "exklusiv", labelKey: "home.tab_exclusive", labelDefault: "Exklusiv", icon: Star,          testId: "tab-exklusiv" },
];

export default function DesktopSuggestions() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const area = useAreaFromPath(pathname);
  const isStx = isSTXContext(pathname);

  const activeTab = useMemo(() => {
    const s = searchParams.get("tab");
    return ["feed", "creator", "exklusiv"].includes(s) ? s : "feed";
  }, [searchParams]);
  const setActiveTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "feed") params.delete("tab"); else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const [creators, setCreators] = useState([]);        // Social-Vorschläge (kompakt)
  const [following, setFollowing] = useState([]);      // Social „Ich folge"
  const [creatorsBusy, setCreatorsBusy] = useState(false);
  const [creatorsRefreshTick, setCreatorsRefreshTick] = useState(0);
  const [stxCreators, setStxCreators] = useState([]);  // STX große Creator-Cards
  const [stxExclusiveNew, setStxExclusiveNew] = useState(0);  // Badge: neue Abo-Beiträge
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    if (!area) return;
    let cancelled = false;
    (async () => {
      try {
        if (area === "FEED") {
          const requests = [api.get(`/home/creators?limit=12&t=${creatorsRefreshTick}`)];
          if (user && user !== false) {
            requests.push(api.get("/me/following?area=SOCIAL"));
          }
          const results = await Promise.allSettled(requests);
          if (cancelled) return;
          if (results[0].status === "fulfilled") setCreators(results[0].value.data?.creators || results[0].value.data || []);
          if (results[1]?.status === "fulfilled") {
            setFollowing(results[1].value.data?.follows || []);
          }
        } else if (area === "STX") {
          const requests = [
            api.get("/stilltouchedx/feed?sort=newest&limit=5"),
            api.get(`/stx/creators?limit=12&t=${creatorsRefreshTick}`),
          ];
          if (user && user !== false) {
            let since = "";
            try { since = localStorage.getItem("stx_exclusive_last_seen") || ""; } catch { /* ignore */ }
            requests.push(api.get(`/stx/exclusive${since ? `?since=${encodeURIComponent(since)}` : ""}`));
          }
          const results = await Promise.allSettled(requests);
          if (cancelled) return;
          if (results[0].status === "fulfilled") setItems(results[0].value.data?.listings || []);
          if (results[1].status === "fulfilled") setStxCreators(results[1].value.data?.creators || []);
          if (results[2]?.status === "fulfilled") setStxExclusiveNew(results[2].value.data?.new_posts_count || 0);
        } else if (area === "MP") {
          const requests = [api.get("/marketplace/feed?sort=newest&limit=2")];
          if (user && user !== false) {
            requests.push(api.get("/orders/buyer"));
            requests.push(api.get("/studio/payouts/summary"));
            requests.push(api.get("/profile/watchlist"));
          }
          const results = await Promise.allSettled(requests);
          if (cancelled) return;
          if (results[0].status === "fulfilled") setItems(results[0].value.data?.listings || []);
          if (results[1]?.status === "fulfilled") {
            const list = results[1].value.data?.orders || results[1].value.data || [];
            const filtered = Array.isArray(list)
              ? list.filter((o) => (o.area_scope || o.area || "").toUpperCase() === "MARKETPLACE")
              : [];
            setOrders(filtered.slice(0, 2));
          }
          if (results[2]?.status === "fulfilled") setPayoutSummary(results[2].value.data || null);
          if (results[3]?.status === "fulfilled") {
            const list = results[3].value.data?.listings || results[3].value.data?.items || results[3].value.data || [];
            const filtered = Array.isArray(list)
              ? list.filter((w) => (w.area_scope || w.listing?.area_scope || "").toUpperCase() === "MARKETPLACE")
              : [];
            setWatchlist(filtered.slice(0, 2));
          }
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [area, user, creatorsRefreshTick]);

  useEffect(() => {
    const onSeen = () => setStxExclusiveNew(0);
    window.addEventListener("stx-exclusive-seen", onSeen);
    return () => window.removeEventListener("stx-exclusive-seen", onSeen);
  }, []);

  if (!area) return null;

  const asideBg = isStx ? "bg-stx-bg border-stx-border" : "bg-white border-[#E5E5E5]";
  const cardBg = isStx ? "bg-stx-panel border-stx-border" : "bg-[#FAFAFA] border-[#E5E5E5]";
  const heading = isStx ? "text-stx-text-muted" : "text-[#666]";
  const textPrimary = isStx ? "text-stx-text" : "text-[#0A0A0A]";
  const textSecondary = isStx ? "text-stx-text-muted" : "text-[#666]";
  const hoverBg = isStx ? "hover:bg-stx-panel-2" : "hover:bg-[#FAFAFA]";
  const activeBg = isStx ? "bg-stx-panel" : "bg-[#F5F5F5]";

  const showFeedTabs = area === "FEED" && pathname === "/";

  return (
    <aside
      data-testid="desktop-sidebar-right"
      data-stx={isStx ? "1" : "0"}
      className={`hidden 2xl:flex 2xl:sticky 2xl:top-14 2xl:h-[calc(100vh-3.5rem)] flex-col shrink-0 border-l ${asideBg} py-6 px-3 overflow-y-auto gap-6`}
    >
      {(user === false || user === null) && <GuestLoginBox isDark={isStx} />}

      {/* ─────────── FEED-Tabs (nur auf /) ─────────── */}
      {showFeedTabs && (
        <nav data-testid="feed-tabs-desktop" className="flex flex-col gap-1">
          {FEED_TABS.map((it) => {
            const Icon = it.icon;
            const active = activeTab === it.key;
            return (
              <button
                key={it.key}
                data-testid={`right-${it.testId}`}
                onClick={() => setActiveTab(it.key)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  active ? `${activeBg} ${textPrimary} font-semibold` : `${textSecondary} ${hoverBg}`
                }`}
              >
                <Icon size={20} strokeWidth={active ? 2.5 : 1.75} />
                <span className="text-sm">{t(it.labelKey, { defaultValue: it.labelDefault })}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* ─────────── SOCIAL · Vorschläge (kompakt) + Ich folge ─────────── */}
      {area === "FEED" && (
        <>
          {creators.length > 0 && (
            <section data-testid="suggest-creators">
              <div className="flex items-center justify-between mb-2">
                <SectionHeader icon={Sparkles} headingClass={heading}>{t("suggest.creators", { defaultValue: "Vorschläge für dich" })}</SectionHeader>
                <button
                  type="button"
                  data-testid="suggest-creators-refresh"
                  onClick={() => { if (!creatorsBusy) { setCreatorsBusy(true); setCreatorsRefreshTick((x) => x + 1); setTimeout(() => setCreatorsBusy(false), 800); } }}
                  disabled={creatorsBusy}
                  className={`w-7 h-7 -mt-1 rounded-full flex items-center justify-center ${textSecondary} ${hoverBg} disabled:opacity-40`}
                  title={t("desktop_suggestions.reload")}
                  aria-label={t("desktop_suggestions.reload")}
                >
                  <RefreshCw size={13} className={creatorsBusy ? "animate-spin" : ""} />
                </button>
              </div>
              <div className="flex flex-col gap-0.5" data-testid="suggest-creators-list">
                {creators.slice(0, 5).map((c) => (
                  <CompactCreatorRow key={c.person_id || c.handle} creator={c} showFollow testIdPrefix="suggest-creator" />
                ))}
              </div>
            </section>
          )}

          {user && user !== false && following.length > 0 && (
            <section data-testid="suggest-following">
              <SectionHeader icon={Users} headingClass={heading}>{t("suggest.following", { defaultValue: "Ich folge" })}</SectionHeader>
              <div className="flex flex-col gap-0.5" data-testid="suggest-following-list">
                {following.slice(0, 8).map((f) => (
                  <CompactCreatorRow
                    key={f.profile_id}
                    creator={{ person_id: f.profile_id, handle: f.handle, display_name: f.display_name, avatar_url: f.avatar_url }}
                    testIdPrefix="suggest-following-row"
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* ─────────── stilltouchedX · Exklusiv-Button + Neue Artikel + große Creator-Cards ─────────── */}
      {area === "STX" && (
        <>
          <button
            type="button"
            data-testid="stx-exclusive-btn"
            onClick={() => navigate("/stilltouchedx/exklusiv")}
            className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${hoverBg} ${textPrimary} font-semibold`}
          >
            <Star size={18} className="text-stx-accent" />
            <span className="text-sm">{t("stx_exclusive.title", { defaultValue: "Exklusiv" })}</span>
            {stxExclusiveNew > 0 && (
              <span data-testid="stx-exclusive-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-stx-accent text-stx-bg text-[10px] font-bold flex items-center justify-center">
                {stxExclusiveNew > 9 ? "9+" : stxExclusiveNew}
              </span>
            )}
          </button>

          {items.length > 0 && (
            <section data-testid="suggest-stx">
              <SectionHeader icon={TrendingUp} headingClass={heading} noUpper>{t("suggest.stx_items", { defaultValue: "Neu bei stilltouchedX" })}</SectionHeader>
              <ul className="flex flex-col gap-1">
                {items.slice(0, 5).map((l) => (
                  <li key={l.listing_id}>
                    <button
                      data-testid={`suggest-item-${l.listing_id}`}
                      onClick={() => navigate(`/stilltouchedx/${l.listing_id}`)}
                      className={`flex items-center gap-3 w-full px-2 py-2 rounded-lg ${hoverBg} text-left`}
                    >
                      <div className="w-10 h-10 rounded bg-stx-panel-2 overflow-hidden shrink-0">
                        {(l.title_photo_url || l.media_urls?.[0]) && (
                          <img src={l.title_photo_url || l.media_urls?.[0]} alt={l.title || ""} loading="lazy" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${textPrimary} line-clamp-1`}>{l.title}</p>
                        <p className={`text-xs ${textSecondary}`}>{l.price?.toFixed(2)} {l.currency}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {stxCreators.length > 0 && (
            <section data-testid="suggest-stx-creators">
              <SectionHeader icon={Sparkles} headingClass={heading}>{t("suggest.creators", { defaultValue: "Vorschläge für dich" })}</SectionHeader>
              <div className="flex flex-col gap-3" data-testid="suggest-stx-creators-list">
                {stxCreators.slice(0, 6).map((c) => (
                  <CreatorSuggestionCard key={c.person_id || c.handle} creator={c} isStx />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* ─────────── Marketplace ─────────── */}
      {area === "MP" && (
        <>
          {items.length > 0 && (
            <section data-testid="suggest-mp">
              <SectionHeader icon={TrendingUp} headingClass={heading} noUpper>{t("suggest.mp_items", { defaultValue: "Neu im Marketplace" })}</SectionHeader>
              <ul className="flex flex-col gap-1">
                {items.slice(0, 2).map((l) => (
                  <li key={l.listing_id}>
                    <button
                      data-testid={`suggest-item-${l.listing_id}`}
                      onClick={() => navigate(`/marketplace/${l.listing_id}`)}
                      className={`flex items-center gap-3 w-full px-2 py-2 rounded-lg ${hoverBg} text-left`}
                    >
                      <div className="w-10 h-10 rounded bg-[#F5F5F5] overflow-hidden shrink-0">
                        {(l.media_urls?.[0] || l.image_urls?.[0]) && (
                          <img src={l.media_urls?.[0] || l.image_urls?.[0]} alt={l.title || ""} loading="lazy" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${textPrimary} line-clamp-1`}>{l.title}</p>
                        <p className={`text-xs ${textSecondary}`}>{l.price?.toFixed(2)} {l.currency}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {user && user !== false && orders.length > 0 && (
            <section data-testid="suggest-orders">
              <SectionHeader icon={ShoppingBag} headingClass={heading}>{t("suggest.my_orders", { defaultValue: "Meine Bestellungen" })}</SectionHeader>
              <ul className="flex flex-col gap-1">
                {orders.map((o) => (
                  <li key={o.order_id || o.id}>
                    <button
                      data-testid={`suggest-order-${o.order_id || o.id}`}
                      onClick={() => navigate(`/profile/purchases/${o.order_id || o.id}`)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-lg ${cardBg} ${hoverBg} text-left`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${textPrimary} truncate`}>{o.public_order_no || o.order_no || o.order_id?.slice(-8)}</p>
                        <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[9px] font-medium border ${orderStatusColor(o.status)}`}>{orderStatusLabel(o.status)}</span>
                      </div>
                      {o.total != null && <span className={`text-sm font-semibold ${textPrimary}`}>{Number(o.total).toFixed(2)} {o.currency || "EUR"}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {user && user !== false && payoutSummary && (payoutSummary.available_balance != null || payoutSummary.pending_balance != null) && (
            <section data-testid="suggest-payouts">
              <SectionHeader icon={Wallet} headingClass={heading}>{t("suggest.payouts", { defaultValue: "Payouts" })}</SectionHeader>
              <button
                data-testid="suggest-payouts-btn"
                onClick={() => navigate("/studio/payouts")}
                className={`w-full px-3 py-2 border rounded-lg ${cardBg} ${hoverBg} text-left`}
              >
                {payoutSummary.available_balance != null && (
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${textSecondary}`}>{t("suggest.available", { defaultValue: "Verfügbar" })}</span>
                    <span className={`text-sm font-semibold ${textPrimary}`}>{Number(payoutSummary.available_balance).toFixed(2)} {payoutSummary.currency || "EUR"}</span>
                  </div>
                )}
                {payoutSummary.pending_balance != null && (
                  <div className="flex items-center justify-between mt-1">
                    <span className={`text-xs ${textSecondary}`}>{t("suggest.pending", { defaultValue: "Ausstehend" })}</span>
                    <span className={`text-sm font-semibold ${textPrimary}`}>{Number(payoutSummary.pending_balance).toFixed(2)} {payoutSummary.currency || "EUR"}</span>
                  </div>
                )}
              </button>
            </section>
          )}

          {user && user !== false && watchlist.length > 0 && (
            <section data-testid="suggest-favorites">
              <SectionHeader icon={Heart} headingClass={heading}>{t("suggest.favorites", { defaultValue: "Favoriten" })}</SectionHeader>
              <ul className="flex flex-col gap-1">
                {watchlist.map((w) => {
                  const wid = w.listing_id || w.id;
                  const title = w.title || w.listing?.title || "—";
                  const img = w.media_urls?.[0] || w.image_urls?.[0] || w.listing?.media_urls?.[0] || w.listing?.image_urls?.[0] || w.thumbnail_url;
                  return (
                    <li key={wid}>
                      <button
                        data-testid={`suggest-fav-${wid}`}
                        onClick={() => navigate(`/marketplace/${wid}`)}
                        className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg ${hoverBg} text-left`}
                      >
                        <div className="w-10 h-10 rounded bg-[#F5F5F5] overflow-hidden shrink-0">
                          {img && <img src={img} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <p className={`text-sm ${textPrimary} truncate flex-1`}>{title}</p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
    </aside>
  );
}
